import type {
  AgentRunInputState,
  AgentRunStatus,
  ChatItem,
  ToolUseItem,
} from '../../../contracts/harness.types';
import type { ToolCallDisplay } from '../../../contracts/tool-call-display';
import { readToolResultText } from '../../../contracts/toolResult';

import { isVisibleUserMessage } from '../components/projectChatMessage.helpers';
import { TODO_TOOL_NAME } from '../timeline/agent-todo/types';
import type { DelegateTraceEntry } from './computer-use-trace.types';
import { sanitizeComputerUseTraceDetail } from './sanitizeComputerUseTraceDetail';

/** Cap the rendered ladder so a very long task can't flood the DOM. */
export const MAX_TRACE_ENTRIES = 200;

const WORKING_ON_SCREEN_LABEL = 'Working on your screen';

/** A trace entry before the cosmetic `at` timestamp is layered on by the hook. */
export type RawTraceEntry = Omit<DelegateTraceEntry, 'at'>;

export interface DerivedComputerUseProgress {
  /** Current-task ladder, UNTRUNCATED (the hook truncates only for display). */
  entries: RawTraceEntry[];
  /** planner_text + tool_call count over the untruncated current-task slice. */
  stepCount: number;
  /** True when the current task carries a persisted cancellation marker. */
  sawCancellation: boolean;
}

/**
 * Derive the computer-use step ladder from a child run's hydrated timeline
 * `items` plus its run state — the hydrate-then-tail replacement for the old
 * live-only accumulator. Pure and deterministic in its inputs, so
 * every viewer that feeds the same `items`/`run` gets the same ladder.
 *
 * Computer-use children hold one task per turn; a persistent child accepts
 * follow-up tasks as NEW turns on the same run, accumulating prior tasks in
 * the timeline (Projects spawns a fresh single-task child per task today).
 * Scope to the CURRENT task only — the items after the last real task
 * prompt — so a prior task's steps, count, and cancellation never leak into
 * the one on screen.
 */
export const deriveComputerUseProgress = (
  items: ChatItem[],
  run: { status?: AgentRunStatus; input_state?: AgentRunInputState } | null | undefined
): DerivedComputerUseProgress => {
  const currentTask = currentTaskItems(items);
  const entries: RawTraceEntry[] = [];

  for (const item of currentTask) {
    if (item.kind === 'assistant_text') {
      // One item == one planner line (deltas coalesced upstream). Keep the full
      // block; surfaces that want a short line cap it at render.
      const label = item.text.trimStart();
      if (label) entries.push({ kind: 'planner_text', id: item.id, label });
      continue;
    }
    if (item.kind === 'tool_use') {
      // `todo` is internal plan bookkeeping. The plan has its own user-facing
      // UI, so repeating its tool lifecycle in the action ladder adds noise.
      if (item.name === TODO_TOOL_NAME) continue;
      // A `perform_action_batch` expands into one neutral "Step N of M" row per
      // step (for ANY batch — note-push or general); every other tool renders a
      // single tool_call line.
      const stepEntries = batchStepEntries(item);
      if (stepEntries.length) {
        entries.push(...stepEntries);
      } else {
        entries.push({
          kind: 'tool_call',
          id: item.id,
          label: friendlyToolLabel(item.name, item.input, item.toolCallDisplay),
          toolName: item.name,
        });
      }
      const resultEntry = toolResultEntry(item);
      if (resultEntry) entries.push(resultEntry);
      continue;
    }
    if (item.kind === 'turn_cancelled_marker') {
      entries.push({
        kind: 'lifecycle',
        id: item.id,
        label: 'Desktop task cancelled',
      });
    }
    // thinking / user_message / turn_failed / prompt_context_failed → intentionally skipped
    // (parity with the old live-only path).
  }

  const sawCancellation = currentTask.some((i) => i.kind === 'turn_cancelled_marker');
  const sawFailure = currentTask.some((item) => item.kind === 'turn_failed');
  if (!sawCancellation && !sawFailure && isTurnComplete(run)) {
    entries.push({
      kind: 'task_complete',
      id: 'task-complete',
      label: 'Desktop task complete',
    });
  }

  const stepCount = entries.filter(
    (e) =>
      e.kind === 'planner_text' || e.kind === 'tool_call' || e.kind === 'step_progress'
  ).length;

  return { entries, stepCount, sawCancellation };
};

/**
 * Items belonging to the latest task: everything after the last real task
 * prompt. Mid-task the timeline ALSO carries one synthetic empty user message
 * per tool result — the BE persists each client-tool result as its own
 * user-role message (Anthropic-style, BE-confirmed); treating those as
 * boundaries reset the ladder mid-task ("Step 1" over and over).
 * `isVisibleUserMessage` is the same prompt-vs-synthetic rule
 * ProjectTurnTimeline uses for turn grouping.
 */
const currentTaskItems = (items: ChatItem[]): ChatItem[] => {
  const firstUserMessageId = items.find((entry) => entry.kind === 'user_message')?.id;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item && isVisibleUserMessage(item, item.id === firstUserMessageId)) {
      return items.slice(i + 1);
    }
  }
  return items;
};

/**
 * A turn is complete once the run closed, or — for a persistent child that
 * stays `running` and returns to `ready` between tasks — once it goes idle.
 * `turn_running` / `approval_required` are still in progress.
 */
const isTurnComplete = (
  run: { status?: AgentRunStatus; input_state?: AgentRunInputState } | null | undefined
): boolean =>
  run?.status === 'closed' || (run?.status === 'running' && run.input_state === 'ready');

const toolResultEntry = (item: ToolUseItem): RawTraceEntry | undefined => {
  // Status flips complete/error before the result block persists, so read it —
  // not `item.result` — to keep the Done/Failed marker from lagging a render.
  if (item.status === 'complete') {
    return {
      kind: 'tool_result',
      id: `${item.id}:result`,
      label: 'Done',
      toolName: item.name,
    };
  }
  if (item.status === 'error') {
    const detail = sanitizeComputerUseTraceDetail(readToolResultText(item));
    return {
      kind: 'tool_result',
      id: `${item.id}:result`,
      label: 'Step failed',
      isError: true,
      toolName: item.name,
      ...(detail ? { detail } : {}),
    };
  }
  // running / queued / pending_approval / cancelled → no completion marker yet.
  return undefined;
};

function friendlyToolLabel(
  toolName: string,
  input: Record<string, unknown> | undefined,
  toolCallDisplay?: ToolCallDisplay
): string {
  if (toolName === 'computer' || toolName === 'computer_batch') {
    const action = input && typeof input.action === 'string' ? input.action : undefined;
    if (action) return describeAction(action);
    if (toolName === 'computer_batch' && input && Array.isArray(input.actions)) {
      const n = (input.actions as unknown[]).length;
      return `Performing ${n} action${n === 1 ? '' : 's'} on screen`;
    }
    return WORKING_ON_SCREEN_LABEL;
  }
  if (toolName === 'inspect_ui') return WORKING_ON_SCREEN_LABEL;
  if (toolName === 'screenshot') return 'Taking a screenshot';
  // Backend owns the display title for other tools; raw name is only a fallback.
  return toolCallDisplay?.title ?? toolName;
}

function describeAction(action: string): string {
  switch (action) {
    case 'left_click':
    case 'right_click':
    case 'double_click':
    case 'click':
      return 'Clicking';
    case 'type':
      return 'Typing';
    case 'key':
    case 'keypress':
      return 'Pressing keys';
    case 'scroll':
      return 'Scrolling';
    case 'screenshot':
      return 'Looking at the screen';
    case 'wait':
      return 'Waiting a moment';
    default:
      return WORKING_ON_SCREEN_LABEL;
  }
}

const truncate = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max - 1)}…`;

const PERFORM_ACTION_BATCH_TOOL = 'perform_action_batch';

/**
 * The acting field's UI label from a batch step's selector, truncated to the
 * trace convention. `undefined` for a step without a named selector (e.g. a blind
 * wait). PHI-safe: reads `selector.name` (a UI field label) ONLY — never the
 * written value.
 */
const stepFieldLabel = (step: Record<string, unknown>): string | undefined => {
  const selector = step.selector;
  const name =
    typeof selector === 'object' && selector !== null
      ? (selector as Record<string, unknown>).name
      : undefined;
  return typeof name === 'string' && name ? truncate(name, 40) : undefined;
};

/**
 * Per-step rows from a `perform_action_batch` — one neutral "Step N of M: <field>"
 * line per step, for ANY batch (note-push or general). Gated on the tool NAME (the
 * tool that carries `steps`), NOT `isComputerUseNotePush`: that discriminator is
 * DERIVED from the tool name and is true ONLY for the `get_note_push_hints` /
 * `record_note_push_hints` items, NEVER the `perform_action_batch` that actually
 * carries the steps — so gating on it made this ladder dead in prod. Task-agnostic
 * copy renders general and note-push batches identically (also closing the
 * "note-push framing leaks onto a general batch" concern). ALL step types render
 * (set_value / perform_action / wait / wait_for). PHI-safe: the label is the
 * step's `selector.name` (a UI field label) ONLY, truncated — never a value.
 */
function batchStepEntries(item: ToolUseItem): RawTraceEntry[] {
  if (item.name !== PERFORM_ACTION_BATCH_TOOL) return [];
  const steps = item.input.steps;
  if (!Array.isArray(steps)) return [];
  const validSteps = steps.filter(
    (step): step is Record<string, unknown> => typeof step === 'object' && step !== null
  );
  const total = validSteps.length;
  return validSteps.map((step, index) => {
    const field = stepFieldLabel(step);
    const position = `Step ${index + 1} of ${total}`;
    return {
      kind: 'step_progress',
      id: `${item.id}:step:${index}`,
      label: field ? `${position}: ${field}` : position,
      toolName: item.name,
    };
  });
}
